import {createContext, memo, type ReactNode, useCallback, useContext, useMemo, useRef, useState} from 'react'

type InsightsConfigurationPaneContextValue = {
  openPane: (sourceRef?: React.RefObject<HTMLElement>) => void
  closePane: () => void
  isClosing: boolean
  isOpen: boolean
  sourceRef?: React.RefObject<HTMLElement>
}

const InsightsConfigurationPaneContext = createContext<InsightsConfigurationPaneContextValue | null>(null)

export const InsightsConfigurationPaneProvider = memo(function InsightsConfigurationPaneProvider({
  children,
}: {
  children: ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState<boolean>(false)
  const [sourceRef, setSourceRef] = useState<React.RefObject<HTMLElement> | undefined>(undefined)
  const closeTimeout = useRef<number>(0)

  const openPane = useCallback((ref?: React.RefObject<HTMLElement>) => {
    clearTimeout(closeTimeout.current)
    setIsClosing(false)
    setIsOpen(true)
    setSourceRef(ref)
  }, [])

  const closePane = useCallback(() => {
    setIsClosing(true)
    closeTimeout.current = window.setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
    }, 300)
  }, [])

  const contextValue = useMemo(
    () => ({
      openPane,
      closePane,
      isClosing,
      isOpen,
      sourceRef,
    }),
    [openPane, closePane, isClosing, isOpen, sourceRef],
  )

  return (
    <InsightsConfigurationPaneContext.Provider value={contextValue}>
      {children}
    </InsightsConfigurationPaneContext.Provider>
  )
})

export const useInsightsConfigurationPane = () => {
  const context = useContext(InsightsConfigurationPaneContext)
  if (context === null) {
    throw new Error('useInsightsConfigurationPane must be used within a InsightsConfigurationPaneProvider')
  }

  return context
}

try{ InsightsConfigurationPaneContext.displayName ||= 'InsightsConfigurationPaneContext' } catch {}
try{ InsightsConfigurationPaneProvider.displayName ||= 'InsightsConfigurationPaneProvider' } catch {}